import classNames from "classnames";
import React from "react";
import {useTranslation} from "react-i18next";
import {Col, Progress, Row} from "reactstrap";
import {TextObservable} from "../../../stores/observables/textObservable";
import SearchBox, {SearchBoxType} from "../../atoms/input/searchBox";
import "./residentAdminListHeader.sass";
import ButtonIcon from "../../atoms/button/buttonIcon";
import {IconSource} from "../../../shared/enums/iconSource";
import CsvDownloader from "react-csv-downloader";
import {useStores} from "../../../stores/useStores";
import {residentCsvExporterConfigs} from "../../../shared/residentCsvExporterConfigs";
import {IResidentAdminDomainStore} from "../../../stores/domainStore/pages/residentAdminDomainStore";
import {IResidentAdminUiStore} from "../../../stores/uiStore/pages/residentAdminUiStore";

import {ResidentRefiner} from "../../../utils/residentRefiner";

export interface IResidentAdminListHeaderProps {
  /**
   * Indicate that the component is loading. When set to true, a progress bar will be revealed.
   */
  isLoading: boolean;

  /**
   * The callback for residents list search event.
   */
  onSearch?: (searchTerm: string) => void;

  searchTerm?: string;

  // Overrides let the serviced-apartment page reuse this header; default to the independent stores.
  residentAdminDomainStore?: IResidentAdminDomainStore;
  residentAdminUiStore?: IResidentAdminUiStore;
  namespace?: string;
}

/**
 * The header for resident admin list view.
 */
const ResidentAdminListHeader: React.FC<IResidentAdminListHeaderProps> = ({
  isLoading,
  onSearch,
  searchTerm,
  residentAdminDomainStore: injectedDomainStore,
  residentAdminUiStore: injectedUiStore,
  namespace = "residentadmin"
}) => {
  const {t} = useTranslation(["residentadmin", "saresidentadmin"]);
  const {
    domainStore: {independentResidentAdminDomainStore: defaultDomainStore},
    uiStore: {independentResidentAdminUiStore: defaultUiStore, modalStore}
  } = useStores();
  const residentAdminDomainStore = injectedDomainStore ?? defaultDomainStore;
  const residentAdminUiStore = injectedUiStore ?? defaultUiStore;

  const onSearchTermChange = function () {
    if (searchTermText.text === "") {
      onSearch?.(searchTermText.text);
    }
  };

  const searchTermText = new TextObservable(
    searchTerm || "",
    null,
    undefined,
    onSearchTermChange
  );

  const reloadPageAfterDownloadError = (): void => {
    modalStore.openModalWithContent({
      content: t("residentadmin:errors.downloadFailed"),
      title: t("residentadmin:messages.exportFailed"),
      primaryButtonText: t("event:actions.confirmRefresh"),
      centered: true,
      canToggle: false,
      onPrimaryClick: () => {
        modalStore.toggle();
      },
      hideFooterBackground: true,
      footerButtonAlignment: "start"
    });
  };

  const columns = residentCsvExporterConfigs.getColumns(t, namespace);

  const getExportResidents = () => {
    return ResidentRefiner.sort(
      ResidentRefiner.filter(
        residentAdminDomainStore.residents,
        residentAdminUiStore.currentResidentAdminListSearchTerm,
        residentAdminUiStore.statusFilter
      ),
      residentAdminUiStore.currentSortField,
      residentAdminUiStore.currentSortOrder
    ).map(residentCsvExporterConfigs.mapResidentToExportRow);
  };

  /**
   * Returns the filename to be used by the CSV download.
   *
   */
  // Remark: This is a low-compute function, so it is okay to be called in render (without using a memo).
  // Remark: Is there an observable for the currently selected village? Seems to be stored in IUserService which is not observable.
  const getFileName = () => {
    return residentCsvExporterConfigs.getFileName(
      residentAdminDomainStore.getVillageCode(),
      namespace
    );
  };

  return (
    <div className="resident-admin-list-header">
      <Row noGutters>
        <Col xs="auto">
          <span>{t(`${namespace}:labels.listHeader`)}</span>
        </Col>
        <Col xs={4} className="d-flex ml-sm-auto mr-2 ">
          <SearchBox
            value={searchTermText}
            type={SearchBoxType.NonLive}
            placeholder={t("residentadmin:labels.searchPlaceholder")}
            onClear={() => {
              searchTermText.setText("");
            }}
            onClick={() => {
              onSearch?.(searchTermText.text);
            }}
          />
        </Col>
        <Col xs md="auto" className="d-flex">
          <CsvDownloader
            filename={getFileName()}
            extension={residentCsvExporterConfigs.fileExtension}
            wrapColumnChar={residentCsvExporterConfigs.wrapColumnChar}
            columns={columns}
            datas={getExportResidents}
            handleError={() => reloadPageAfterDownloadError()}
          >
            <ButtonIcon type="secondary" icon={IconSource.export}>
              {t("translation:actions.export")}
            </ButtonIcon>
          </CsvDownloader>
        </Col>
      </Row>
      <Row noGutters>
        <Progress
          data-testid={"progressBar"}
          style={{width: "100%", height: "3px"}}
          color="info"
          animated
          className={classNames("resident-admin-progress-bar", {
            "is-loading": isLoading
          })}
          value={isLoading ? 95 : 0}
        />
      </Row>
    </div>
  );
};

export default ResidentAdminListHeader;
